// src/slices/authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface User {
    id: number;
    email: string;
    fullName: string;
    isAdmin: boolean;
    isPremium: boolean;
    // Optional on purpose: the login response (UserAuthResponseDto) does not carry this field, so
    // it is undefined until a screen refreshes the user from GET /account/{id} (AccountDto), which
    // LatestPosts does. Consumers must therefore distinguish "false" from "not loaded".
    canComment?: boolean;
}

interface AuthState {
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    user: User | null;
}

const initialState: AuthState = {
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    user: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginSuccess: (state, action: PayloadAction<{ accessToken: string; refreshToken: string; user: User }>) => {
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
            state.isAuthenticated = true;
            state.user = action.payload.user;

            // Stays in localStorage on purpose. sessionStorage was considered and rejected: an
            // XSS that can read this token exfiltrates it the moment it runs, so the storage choice
            // does not change what an attacker gets — it only decides whether every member has to
            // sign in again each time they close the browser. On a subscription site people open
            // daily, that is a real, certain cost against a hypothetical one (the audit found no XSS
            // sink in this app: no dangerouslySetInnerHTML, all content rendered as JSX text, the one
            // HTML surface sandboxed). The genuine fix is an httpOnly cookie, which needs a backend
            // change and CSRF protection back on — see 06-frontend-hardening.md.
            localStorage.setItem('accessToken', state.accessToken);
            localStorage.setItem('user', JSON.stringify(state.user));
            // The refresh token is deliberately NOT persisted. Per 03-architecture-review.md §3.1 no
            // client ever calls POST /api/v1/auth/refresh-token — it is minted by the server and
            // never used, so storing it is a second long-lived credential lying around for no
            // functional benefit. It stays in memory for this tab, matching the response shape.
        },
        logout: (state) => {
            state.accessToken = null;
            state.refreshToken = null;
            state.isAuthenticated = false;
            state.user = null;

            // Remove from local storage. sessionStorage is cleared too: a browser that ran the
            // interim build which stored the token there must not keep a stale copy after logout.
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
        },
        updateUser: (state, action: PayloadAction<User>) => {
            if (state.user) {
                state.user = { ...state.user, ...action.payload };

                localStorage.setItem('user', JSON.stringify(state.user));
            }
        },
    },
});

export const { loginSuccess, logout, updateUser } = authSlice.actions;
export default authSlice.reducer;
